import { createSlice } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { SOURCE_TCAPI } from '@rsos/constants/locationSources';
import {
  ConversationErrorRecord,
  ConversationLoadingRecord,
  conversationStateValues,
} from './conversations.typings';
import { tcapiAPI } from './tcapiAPI';

/**
 * A Redux slice that contains and bundles all possible conversations and their data together
 * Only stores 1 main conversation at a time
 */
const conversationsSlice = createSlice({
  name: 'conversations',
  initialState: {} as Record<string, conversationStateValues>,
  reducers: {
    setConversationUnread: (state, action) => {
      const { isUnread, callerID } = action.payload;

      if (state[callerID]) {
        state[callerID] = {
          ...state[callerID],
          isUnread: isUnread,
        };
      }
    },
    setIsConversationCreated: (state, action) => {
      const callerID = action.payload;
      const conversation = state[callerID];

      if (conversation && conversation.data) {
        if (conversation.source === SOURCE_TCAPI) {
          conversation.data.acceptStatus == 'accepted';
          conversation.data.status = 'active';
        }
      }
    },
    setIsConversationEnded: (state, action) => {
      const callerID = action.payload;
      const conversation = state[callerID];

      if (conversation && conversation.data) {
        if (conversation.source === SOURCE_TCAPI) {
          conversation.data.status = 'inactive';
        }
      }
    },
    addConversation: (state, action) => {
      const body = action.payload;
      const callerID = body?.caller_id;

      if (callerID) {
        state[callerID] = {
          data: body,
          source: body.source,
          isUnread: false,
          error: {} as ConversationErrorRecord,
          loading: {} as ConversationLoadingRecord,
        };
      }
    },
    clearConversation: (state, action) => {
      const callerID = action.payload;
      delete state[callerID];
    },
  },
  extraReducers: builder => {
    builder
      .addMatcher(
        tcapiAPI.endpoints.getTCAPIConversationMetadata.matchFulfilled,
        (state, action) => {
          const callerID: string = action.meta.arg.originalArgs.callerID;
          const existingConversation = state[callerID];

          // Replace stale inactive TCAPI session with new active/pending session data
          // This prevents old ended sessions from blocking new sessions on the same number
          if (
            existingConversation?.source === SOURCE_TCAPI &&
            existingConversation?.data?.status === 'inactive' &&
            action.payload?.status !== 'inactive'
          ) {
            delete state[callerID];
          }

          // Keep inactive sessions in state to allow UI to hide buttons and show "Closed Chat" status
          state[callerID] = {
            ...state[callerID],
            source: SOURCE_TCAPI,
            data: action.payload,
            loading: {
              ...state?.[callerID]?.loading,
              getConversationMetadata: false,
            },
          };
        },
      )
      .addMatcher(
        tcapiAPI.endpoints.getTCAPIConversationMetadata.matchPending,
        (state, action) => {
          const callerID: string = action.meta.arg.originalArgs.callerID;

          state[callerID] = {
            ...state?.[callerID],
            source: SOURCE_TCAPI,
            data: null,
            error: {
              ...state?.[callerID]?.error,
              getConversationMetadata: null,
            },
            loading: {
              ...state?.[callerID]?.loading,
              getConversationMetadata: true,
            },
            isUnread: false,
          };
        },
      )
      .addMatcher(
        tcapiAPI.endpoints.getTCAPIConversationMetadata.matchRejected,
        (state, action) => {
          const callerID: string = action.meta.arg.originalArgs.callerID;

          // NOTE: We intentionally delete the conversation entry here rather than following
          // the typical pattern of preserving the entry with error state. This is because:
          // 1. A 404 response indicates the TCAPI session legitimately doesn't exist (not a temporary error)
          // 2. Stale TCAPI state can persist from previous sessions (e.g., Redux persist or page state)
          // 3. We need conversationSource to be falsy so CallChats.tsx renders the correct component
          //    (TF911ChatBox for TF911 sessions, not ConversationsChatBox)
          // 4. Keeping source="tcapi" with data=null would cause incorrect routing to TCAPI APIs
          //    for non-TCAPI sessions
          // By deleting the entry, we allow the UI to fall back to the appropriate chat component
          // based on the actual session type (TF911, RCS, etc.)
          delete state[callerID];
        },
      );
  },
});

const persistConfig = {
  key: 'conversation',
  storage,
};

const persistedReducer = persistReducer(
  persistConfig,
  conversationsSlice.reducer,
);

export const {
  setConversationUnread,
  setIsConversationCreated,
  setIsConversationEnded,
  addConversation,
  clearConversation,
} = conversationsSlice.actions;

export default persistedReducer;
